import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Camera, useCameraDevice } from 'utils/packages/Camera';
import { triggerHapticFeedback } from 'utils/hapticFeedback';
import { Animated, View, Image, Platform, StatusBar } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import Button from 'design-system/Button';
import Text from 'design-system/Text';
import Spacer from 'design-system/Spacer';
import IconFlash from 'design-system/icons/IconFlash';
import { rem } from 'design-system/values';
import NavigationHeaderRightIconButton from 'design-system/NavigationHeaderRightIconButton';
import useStyles from 'hooks/useStyles';
import { useMarginBottom } from 'hooks/useMarginBottom';
import createStyleSheets from 'utils/createStyleSheets';
import { useFocusEffect, useHeaderHeight } from 'hooks';
import IconFlipCamera from '../icons/IconFlipCamera';
import useCameraPermission from '../hooks/useCameraPermission';
const strings = {
    title: 'Upload proof of address',
    confirmTitle: 'Be sure your document shows:',
    confirmText: ` - Your address
 - Your name
 - Legible and recent info (past 3 months)`,
    upload: 'Continue',
    retake: 'Retake',
    uploading: 'Uploading Photo...',
    cameraNotWorking: 'Sorry, but your camera is\xa0not\xa0working.',
    cameraNotWorking2: 'Try to flip the camera?',
    flip: 'Flip camera',
    noAccessTitle: 'Camera Check',
    noAccess: 'Access to the camera has been prohibited.\nPlease enable it in the settings to continue.',
    noAccessButton: 'Open settings',
};
const VIEW_FINDER_HORIZONTAL_PADDING = rem(16);
const ScanProofOfAddressScreen = ({ route, navigation, }) => {
    const flow = route.params?.flow;
    const address = route.params?.address;
    const styles = useStyles(styleSet);
    useLayoutEffect(() => {
        navigation.setOptions({
            title: 'Verify address',
            headerRight: () => route.params?.showTorch ? (<NavigationHeaderRightIconButton onPress={() => {
                    ScanProofOfAddressScreen.onToggleTorch();
                }}>
            <IconFlash />
          </NavigationHeaderRightIconButton>) : null,
        });
    }, [route.params?.hideTitle, route.params?.showTorch]);
    const frontCamera = useCameraDevice('front');
    const backCamera = useCameraDevice('back');
    const [focused, setFocused] = useState(false);
    const [path, setPath] = useState(); // path to saved photo
    const [torch, setTorch] = useState('off');
    const [front, setFront] = useState(false); // front or back camera to use
    const camera = useRef(null);
    const blackOverlayOpacity = useRef(new Animated.Value(0));
    const buttonsContainerOpacity = useRef(new Animated.Value(0));
    const [accessAllowed, openSettings] = useCameraPermission();
    // useEffect(() => {
    //   const prepare = async () => {
    //     const devices = await Camera.getAvailableCameraDevices();
    //     // TODO: pick camera with best resolution
    //   };
    //   prepare();
    // }, []);
    useEffect(() => {
        if (front) {
            if (frontCamera?.hasTorch) {
                navigation.setParams({ showTorch: true });
            }
            else {
                navigation.setParams({ showTorch: false });
            }
        }
        else if (backCamera?.hasTorch) {
            navigation.setParams({ showTorch: true });
        }
        else {
            navigation.setParams({ showTorch: false });
        }
    }, [front, path, backCamera?.hasTorch, frontCamera?.hasTorch]);
    useFocusEffect(useCallback(() => {
        setFocused(true);
        setPath(undefined);
        blackOverlayOpacity.current.setValue(0);
        return () => {
            setFocused(false);
        };
    }, []));
    const animateTo = useCallback((value, callback) => {
        if (value === 1) {
            Animated.sequence([
                Animated.timing(blackOverlayOpacity.current, {
                    toValue: 1,
                    duration: 200,
                    useNativeDriver: true,
                }),
                Animated.timing(buttonsContainerOpacity.current, {
                    toValue: 1,
                    duration: 200,
                    useNativeDriver: true,
                }),
            ]).start(callback);
        }
        else if (value === 0) {
            Animated.sequence([
                Animated.timing(buttonsContainerOpacity.current, {
                    toValue: 0,
                    duration: 200,
                    useNativeDriver: true,
                }),
                Animated.timing(blackOverlayOpacity.current, {
                    toValue: 0,
                    duration: 200,
                    useNativeDriver: true,
                }),
            ]).start(callback);
        }
    }, []);
    const onTakePhoto = useCallback(async () => {
        if (camera.current) {
            triggerHapticFeedback('impactLight');
            const photo = await camera.current.takePhoto();
            const finalWidth = photo.orientation.includes('landscape') ? photo.height : photo.width;
            const finalHeight = photo.orientation.includes('landscape') ? photo.width : photo.height;
            setPath({ path: `file://${photo.path.replace('file://', '')}`, width: finalWidth, height: finalHeight });
            animateTo(1, () => {
                navigation.navigateDeprecated('InvestProofOfAddressPreview', {
                    flow,
                    address,
                    width: finalWidth,
                    isFromCamera: true,
                    height: finalHeight,
                    uri: `file://${photo.path.replace('file://', '')}`,
                });
            });
        }
    }, [address, animateTo, flow]);
    const onFlipCamera = useCallback(() => {
        setFront((currentState) => !currentState);
    }, []);
    ScanProofOfAddressScreen.onToggleTorch = useCallback(() => {
        triggerHapticFeedback('impactLight');
        setTorch((currentState) => (currentState === 'off' ? 'on' : 'off'));
    }, []);
    const cameraView = useMemo(() => {
        if (accessAllowed === false) {
            return (<View style={[styles.cameraPreview, styles.notWorking]}>
          <Text Text-20 White centered>
            {strings.noAccessTitle}
          </Text>
          <Spacer h={8}/>
          <Text TextThin-14 White centered>
            {strings.noAccess}
          </Text>
          <Spacer h={32}/>
          <Button brand style={styles.noAccessButton} title={strings.noAccessButton} onPress={openSettings}/>
        </View>);
        }
        if (Platform.OS === 'android' && accessAllowed === undefined) {
            return <View style={styles.cameraPreview}/>; // on android, we need to wait until we are sure that we have access to camera
        }
        if (path) {
            return <Image style={styles.cameraPreview} source={{ uri: path.path }}/>;
        }
        if (front && frontCamera) {
            return (<Camera ref={camera} style={styles.cameraPreview} device={frontCamera} isActive={focused} photo torch={torch}/>);
        }
        if (backCamera) {
            return (<Camera ref={camera} style={styles.cameraPreview} device={backCamera} isActive={focused} photo torch={torch}/>);
        }
        return (<View style={[styles.cameraPreview, styles.notWorking]}>
        <Text White centered Text-20>
          {strings.cameraNotWorking}
        </Text>
        <Spacer h={30}/>
        <Text White centered TextThin-16>
          {strings.cameraNotWorking2}
        </Text>
        <Spacer h={20}/>
        <Button brand style={styles.noAccessButton} title={strings.flip} onPress={onFlipCamera}/>
      </View>);
    }, [
        accessAllowed,
        path,
        front,
        frontCamera,
        backCamera,
        styles.cameraPreview,
        styles.notWorking,
        styles.noAccessButton,
        onFlipCamera,
        openSettings,
        focused,
        torch,
    ]);
    const bottomWithOffset = useMarginBottom('bottom', rem(30), styles.flipCameraContainer);
    const paddingBottom = useMarginBottom('paddingBottom', rem(30), styles.controlsContainer);
    const controlsContainerStyle = useMemo(() => [
        paddingBottom,
        {
            opacity: blackOverlayOpacity.current.interpolate({
                inputRange: [0, 1],
                outputRange: [1, 0],
            }),
        },
    ], [paddingBottom]);
    const headerHeight = useHeaderHeight();
    return (<View style={styles.container}>
      <StatusBar barStyle="dark-content" animated/>
      {cameraView}
      <View style={[{ height: headerHeight }, styles.header]}/>
      <Animated.View style={styles.textsContainer} pointerEvents="none">
        <Text Text-20 White>
          {strings.title}
        </Text>
        <Spacer h={8}/>
        <Text TextThin-14 White>
          The whole document must be in the frame. Make sure all the details are visible.
        </Text>
      </Animated.View>
      <Animated.View style={controlsContainerStyle} pointerEvents={path || !accessAllowed ? 'none' : 'auto'}>
        <View style={styles.shutterContainer}>
          <TouchableBounce style={styles.shutter} onPress={onTakePhoto}/>
        </View>
        <View style={bottomWithOffset}>
          <TouchableBounce onPress={onFlipCamera}>
            <IconFlipCamera />
          </TouchableBounce>
        </View>
      </Animated.View>
    </View>);
};
const styleSet = createStyleSheets(() => ({
    container: {
        flex: 1,
        backgroundColor: 'black',
    },
    cameraPreview: {
        flex: 1,
    },
    notWorking: {
        justifyContent: 'center',
        padding: rem(20),
    },
    textsContainer: {
        flex: 0,
        padding: VIEW_FINDER_HORIZONTAL_PADDING,
        paddingBottom: rem(40),
    },
    controlsContainer: {
        alignItems: 'center',
    },
    shutterContainer: {
        width: 64,
        height: 64,
        borderRadius: 32,
        borderWidth: 4.5,
        borderColor: 'white',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#17102A',
    },
    shutter: {
        width: 50,
        height: 50,
        borderRadius: 25,
        backgroundColor: 'white',
    },
    flipCameraContainer: {
        position: 'absolute',
        top: 0,
        right: rem(16),
        justifyContent: 'center',
    },
    noAccessButton: {
        alignSelf: 'center',
        paddingHorizontal: rem(60),
    },
    header: {
        opacity: 0.8,
        left: 0,
        right: 0,
        top: 0,
        position: 'absolute',
        backgroundColor: '#17102A',
    },
}));
ScanProofOfAddressScreen.onToggleTorch = () => { };
export default ScanProofOfAddressScreen;
